import React, { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getCommonTextFilterParams,
  getSetFilterParams,
  getSetDatefilterParams,
  getSetCheckboxFilterParams,
} from '../../utils/agGridFilters';
import DeleteConfirmationModal from '../../shared/modal/deleteConfirmation';
import { DateTimeFormatter, NameFormatter } from '../../shared/controls/formatters';
import { currencyFormatterByField } from '../../shared/constants';

const OptionButton = ({
  handleOpenEditForm,
  paramsValue,
  handleOpenViewForm,
  rowData,
  handleClickDelete,
  hasDeleteAccountPermission,
  hasUpdateAccountPermission,
  hasInfoAccountPermission,
}) => {
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleDeleteClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmDelete = () => {
    handleClickDelete(paramsValue);
    setShowConfirmation(false);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="action-menu">
      {hasUpdateAccountPermission && (
        <img
          src="/assets/images/edit.png"
          className="action-img1"
          onClick={() => handleOpenEditForm(paramsValue)}
          alt="Edit Account"
          title="Edit Account"
        ></img>
      )}
      {hasInfoAccountPermission && (
        <img
          src="/assets/images/view.png"
          className="action-img2"
          onClick={() => handleOpenViewForm(paramsValue)}
          alt="View Account"
          title="View Account"
        ></img>
      )}
      {hasDeleteAccountPermission && (
        <img
          src="/assets/images/trash.png"
          className="action-img2"
          onClick={handleDeleteClick}
          alt="Delete Account"
          title="Delete Account"
        ></img>
      )}
      <DeleteConfirmationModal
        show={showConfirmation}
        handleClose={handleCloseConfirmation}
        handleConfirm={handleConfirmDelete}
        rowData={rowData}
      />
    </div>
  );
};

const useAccountColumnDefs = ({
  hasUpdateAccountPermission,
  hasInfoAccountPermission,
  hasDeleteAccountPermission,
  gridData,
  handleClickDelete,
  tagsList,
  owner,
  accountType,
  industryList,
}) => {
  const navigate = useNavigate();
  const commonTextFilterParams = useMemo(() => getCommonTextFilterParams(), []);
  const dateFilterParams = useMemo(() => getSetDatefilterParams(), []);
  const checkboxFilterParams = useMemo(() => getSetCheckboxFilterParams, []);
  const filterParams = useMemo(() => getSetFilterParams, []);
  const handleOpenEditForm = (paramsValue) => {
    navigate('/accounts/editAccount', {
      state: paramsValue,
    });
  };

  const handleOpenViewForm = (paramsValue) => {
    navigate(
      `/accounts/viewAccount?id=${paramsValue.id}&pageNo=${gridData?.inputPayload?.pageNumber}`,
      {
        state: paramsValue,
      }
    );
  };

  const actionsColumn = useMemo(() => {
    if (!(hasUpdateAccountPermission || hasInfoAccountPermission || hasDeleteAccountPermission))
      return [];

    return [
      {
        headerName: 'Actions',
        headerCheckboxSelection: (params) => params?.node && !params.node.group,
        checkboxSelection: (params) => params?.node && !params.node.group,
        headerCheckboxSelectionFilteredOnly: true,
        cellRenderer: (params) => {
          if (!params?.node || params.node.group) return null;
          const rowData = { ...params.data, rowIndex: params.rowIndex };
          return (
            <OptionButton
              rowData={rowData}
              hasInfoAccountPermission={hasInfoAccountPermission}
              hasDeleteAccountPermission={hasDeleteAccountPermission}
              hasUpdateAccountPermission={hasUpdateAccountPermission}
              handleOpenEditForm={() => handleOpenEditForm(rowData)}
              handleOpenViewForm={() => handleOpenViewForm(rowData)}
              handleClickDelete={() => handleClickDelete(rowData)}
            />
          );
        },
        maxWidth: 100,
        minWidth: 100,
        sortable: false,
        filter: false,
        pinned: 'left',
      },
    ];
  }, [
    hasUpdateAccountPermission,
    hasInfoAccountPermission,
    hasDeleteAccountPermission,
    handleOpenEditForm,
    handleOpenViewForm,
    handleClickDelete,
  ]);

  const columnDefs = useMemo(() => {
    return [
      ...actionsColumn,
      {
        headerName: 'Account Name',
        field: 'name',
        type: 'text',
        cellRenderer: (params) => {
          if (!params.value) return null; // Handle empty or null values
          return hasInfoAccountPermission ? (
            <Link
              to={`/accounts/viewAccount?id=${params?.data?.id}&pageNo=${gridData?.inputPayload?.pageNumber}`}
              state={params.data}
              style={{ fontWeight: '600' }}
            >
              {params.value}
            </Link>
          ) : (
            <span style={{ fontWeight: '600' }}>{params.value}</span>
          );
        },
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        minWidth: 180,
      },
      {
        headerName: 'Phone',
        field: 'phone',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Alternate Phone',
        type:"text",
        field: 'alternatePhone',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Website',
        field: 'website',
        type:"text",
        filter: 'agTextColumnFilter',
        cellRenderer: (params) => {
          if (!params.value) return null;

          // Ensure URL has protocol for the link
          const url = params.value.startsWith('http') ? params.value : `https://${params.value}`;
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {params.value}
            </a>
          );
        },
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Account Owner',
        field: 'owner',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(owner?.map((item) => item.display_name)),
      },
      {
        headerName: 'Account Type',
        field: 'accountType',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(accountType?.map((item) => item.displayName)),
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Industry',
        field: 'industry',
        type: 'dropdown',
        filter: 'agSetColumnFilter',
        filterParams: filterParams(industryList?.map((item) => item.displayName)),
      },
      // {
      //   headerName: 'Business Type',
      //   field: 'businessType',
      //   filter: 'agTextColumnFilter',
      //   filterParams: commonTextFilterParams,
      // },
      {
        headerName: 'Employees',
        field: 'employees',
        type: 'text',
        filter: 'agNumberColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Annual Revenue',
        field: 'annualRevenue',
        type: 'text',
        filter: 'agNumberColumnFilter',
        filterParams: commonTextFilterParams,
        valueFormatter: currencyFormatterByField('annualRevenue'),
      },
      {
        headerName: 'Company LinkedIn',
        field: 'companyLinkedIn',
        type: 'text',
        filter: 'agTextColumnFilter',
        cellRenderer: (params) => {
          if (!params.value) return null;

          // Ensure URL has protocol for the link
          const url = params.value.startsWith('http') ? params.value : `https://${params.value}`;

          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {params.value}
            </a>
          );
        },
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'State',
        field: 'state',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Zip Code',
        field: 'zipCode',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Country',
        field: 'country',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Created By',
        field: 'createdBy',
        valueFormatter: NameFormatter,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        minWidth: 180,
      },
      {
        headerName: 'Updated By',
        field: 'updatedBy',
        valueFormatter: NameFormatter,
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
        minWidth: 180,
      },
      {
        headerName: 'Created Date Time (UTC)',
        field: 'createdDateTime',
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        minWidth: 230,
      },
      {
        headerName: 'Updated Date Time (UTC)',
        field: 'updatedDateTime',
        valueFormatter: DateTimeFormatter,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
        minWidth: 230,
      },
      {
        headerName: 'Tags',
        field: 'tagIds',
        valueGetter: (params) => params?.data?.tags,
        filter: 'agSetColumnFilter',
        filterParams: filterParams(tagsList?.map((item) => item.displayName)),
      },
      {
        headerName: 'Parent Account',
        field: 'parentAccount',
        type: 'dropdown',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Description',
        field: 'description',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Lead',
        field: 'lead',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'No. of Beds',
        field: 'noOfBeds',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Street',
        field: 'street',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'City',
        field: 'city',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
      {
        headerName: 'Technology Details',
        field: 'technologyDetails',
        type: 'text',
        filter: 'agTextColumnFilter',
        filterParams: commonTextFilterParams,
      },
    ];
  }, [
    hasInfoAccountPermission,
    gridData,
    actionsColumn,
    commonTextFilterParams,
    dateFilterParams,
    checkboxFilterParams,
    filterParams,
    industryList,
    tagsList,
    owner,
    accountType,
  ]);

  return columnDefs;
};

export default useAccountColumnDefs;
